package fr.arcplex.sooner.webui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.model.Screen
import fr.arcplex.network.model.ScreenKind
import fr.arcplex.network.model.isSearchRail
import fr.arcplex.network.model.normalizeRailId
import fr.arcplex.sooner.VideoInfo

/**
 * UI state of the displayed screen: the port's [Screen] plus the in-place
 * search refresh, which only replaces the items of one rail.
 */
class Page(
    val url: String?,
    val type: String,
    val label: String,
    val bgImage: String?,
    var bgColor: String?,
    var menu: Rail,
    var fixed_element: Rail,
    rails: List<Rail>
) {
    var rails by mutableStateOf(rails)
        private set

    /**
     * Replaces the items of the rail named by [idRail] with [items].
     * Returns false when no rail matches, so callers can log ignored refreshes.
     */
    fun updateRail(idRail: String, items: List<RailItem>): Boolean {
        val target = normalizeRailId(idRail)
        val index = rails.indexOfFirst {
            it.id == idRail || normalizeRailId(it.id) == target
        }
        if (index < 0) return false
        rails = rails.toMutableList().also { list ->
            list[index] = list[index].copy(items = items)
        }
        return true
    }

    fun getVideoInformation() : VideoInfo? {
        val isVideo = rails.any {
            it.id.startsWith("title", true)
                    || it.id.startsWith("because", true)
                    || it.id.startsWith("casting", true)
                    || it.id.startsWith("resume", true)
        }
        if(!isVideo) {
            return null
        }

        var info = VideoInfo()
        info.title = rails.firstOrNull { it.id.startsWith("title", true) }?.items?.firstOrNull()?.content ?: ""
        info.description = rails.firstOrNull { it.id.startsWith("resume", true) }?.items?.firstOrNull()?.content ?: ""
        val artUrl = rails.asSequence()
            .flatMap { it.items.asSequence() }
            .firstOrNull { it.type == "image" && it.content.isNotBlank() }
            ?.content
        if(artUrl != null) {
            info.artworkUrl = artUrl
        }

        info.because = rails.firstOrNull { it.id.startsWith("because", true) }?.items?.firstOrNull()?.content
        info.infostech = rails.firstOrNull { it.id.startsWith("infostech", true) }?.items?.firstOrNull()?.content

        info.tags = rails.firstOrNull { it.id.startsWith("tags", true) }?.items?.map { it.content } ?: listOf<String>()
        val cast = rails.firstOrNull { it.id.startsWith("cast", true) }
        if(cast != null && cast.items.isNotEmpty()) {
            info.director = cast.items.firstOrNull()?.content
            if(cast.items.size > 1) {
                info.casting = cast.items.drop(1).map { it.content }
            }
        }

        info.actions = rails.firstOrNull { it.id.startsWith("actions") }?.items?.mapNotNull { it.action?.url } ?: listOf()

        return info
    }

    companion object {
        /** Adapts the port's screen to the UI state consumed by the views. */
        fun from(screen: Screen): Page = Page(
            url = screen.id,
            type = if (screen.kind == ScreenKind.FILM) "FILM" else "NORMAL",
            label = screen.label,
            bgImage = screen.backgroundImage,
            bgColor = screen.backgroundHex,
            menu = screen.menu,
            fixed_element = screen.hero,
            rails = screen.rails
        )
    }
}

/** Adapts the UI state back to the model for item-action classification. */
fun Page.toScreen(): Screen = Screen(
    id = url,
    kind = when {
        type.equals("FILM", ignoreCase = true) -> ScreenKind.FILM
        isSearchPage -> ScreenKind.SEARCH
        else -> ScreenKind.NORMAL
    },
    label = label,
    backgroundImage = bgImage,
    backgroundHex = bgColor,
    menu = menu,
    hero = fixed_element,
    rails = this.rails
)

val Page.isSearchPage: Boolean
    get() = rails.any { it.isSearchRail }

fun parseColor(hex: String): Color {
    return try {
        val clean = hex.replace("#", "")
        when (clean.length) {
            6 -> {
                val r = clean.substring(0, 2).toInt(16)
                val g = clean.substring(2, 4).toInt(16)
                val b = clean.substring(4, 6).toInt(16)
                Color(r or (g shl 8) or (b shl 16)) // Corrected color component order for Compose Color constructor
            }
            8 -> {
                val a = clean.substring(0, 2).toInt(16)
                val r = clean.substring(2, 4).toInt(16)
                val g = clean.substring(4, 6).toInt(16)
                val b = clean.substring(6, 8).toInt(16)
                Color(a, r, g, b)
            }
            else -> Color(0xFF0A0E17)
        }
    } catch (e: Exception) {
        Color(0xFF0A0E17)
    }
}
